import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Clock,
  Save,
  AlertTriangle,
  DollarSign,
  FileText,
  ChevronRight,
  Trash2,
  Calendar,
  RefreshCw
} from 'lucide-react';
import Layout from '../components/Layout';
import Button from '../components/Button';
import Alert from '../components/Alert';
import { useClaimStore } from '../store/claimStore';
import { useEmbeddedApp } from '../providers/EmbeddedAppProvider';
import { useScreenNavigation } from '../hooks/useScreenNavigation';
import { getStatusLabelKey } from '../utils/claimStatusDisplay';
import { RequestType } from '../types';
import type { Claim } from '../types/interfaces';
import { logger } from '../utils/logger';

interface ExistingClaimCheck {
  type: 'NOVA' | 'DRAFT' | 'NONE';
  claims?: Partial<Claim>[];
  drafts?: Array<{ id: string; data: any; lastSaved: Date; progress: number }>;
}

const SCREEN_CODE = '00-01-NOUVELLE';

const PrepareClaimScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getDrafts, loadDraft, deleteDraft } = useClaimStore();
  const { parentContext, isEmbedded } = useEmbeddedApp();
  useScreenNavigation(SCREEN_CODE);

  const [checkResult, setCheckResult] = useState<ExistingClaimCheck>({ type: 'NONE' });
  const [isChecking, setIsChecking] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState<string | null>(null);

  // Don't check for existing claims on mount - just show the preparation screen
  // The check will happen if user explicitly asks to continue a draft from home screen
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Skip the check - always show preparation content
    setCheckResult({ type: 'NONE' });
    setIsChecking(false);
  }, []);

  const checkExistingClaims = async () => {
    setIsChecking(true);

    try {
      // 1. Check NOVA for in-progress claims (via parent context)
      if (isEmbedded && parentContext?.existingClaims) {
        const inProgressClaims = parentContext.existingClaims.filter(
          claim => claim.status === 'DRAFT' || claim.status === 'SUBMITTED'
        );

        if (inProgressClaims.length > 0) {
          setCheckResult({
            type: 'NOVA',
            claims: inProgressClaims
          });
          setIsChecking(false);
          return;
        }
      }

      // 2. Check local drafts — only ACTIVE ones (filter abandoned/cancelled out)
      const localDrafts = getDrafts().filter((d: any) => getStatusLabelKey(d?.status) !== 'cancelled');
      if (localDrafts.length > 0) {
        const draftsWithProgress = localDrafts.map((draft: any) => ({
          id: draft.id,
          data: draft,
          lastSaved: new Date(draft.lastSaved || Date.now()),
          progress: calculateProgress(draft)
        }));

        setCheckResult({
          type: 'DRAFT',
          drafts: draftsWithProgress
        });
        setIsChecking(false);
        return;
      }

      // 3. No existing claims or drafts
      setCheckResult({ type: 'NONE' });
    } catch (error) {
      logger.error('Error checking existing claims', {
        errorType: 'CheckExistingClaimsError',
        screen: 'PrepareClaimScreen'
      }, error as Error);
      setCheckResult({ type: 'NONE' });
    } finally {
      setIsChecking(false);
    }
  };

  const calculateProgress = (draft: any): number => {
    const completedSteps = draft.workflow?.completedSteps?.length || 0;
    const totalSteps = draft.workflow?.totalSteps || 20;
    return Math.round((completedSteps / totalSteps) * 100);
  };

  const handleContinueNewClaim = () => {
    // Clear any existing claim data to ensure fresh start
    const { clearClaim, setDeferClaimCreation } = useClaimStore.getState();
    clearClaim();

    // Defer claim initialization until after 01-01-CONTRAT (contract selection)
    // This ensures the claim is created WITH contract information from the start
    setDeferClaimCreation(true);

    navigate('/claim/00-02-AVIS');
  };

  const handleContinueNovaClaim = (claim: Partial<Claim>) => {
    // Navigate to claim details view to continue NOVA claim
    navigate(`/claim/details/${claim.id}`);
  };

  const handleResumeDraft = (draftId: string) => {
    loadDraft(draftId);
    const draft = checkResult.drafts?.find(d => d.id === draftId);
    if (draft) {
      // Navigate to the last incomplete step
      const lastStep = draft.data.workflow?.currentStep || 1;
      navigate(`/claim/step/${lastStep}`);
    }
  };

  const handleDeleteDraft = async (draftId: string) => {
    if (window.confirm(t('steps.prepare.deleteDraftConfirm', 'Deletedraftconfirm'))) {
      deleteDraft(draftId);
      await checkExistingClaims(); // Re-check after deletion
    }
  };

  const handleViewDrafts = () => {
    navigate('/', {
      replace: true
    });
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderInfoSection = () => {
    // "Demandes en cours détectées" panel — only show ACTIVE drafts (not
    // user-abandoned ones). Filters out ABANDONED, RESUMPTION_ABANDONED, plus
    // any of the cancelled-family wire values via the canonical label key.
    const drafts = getDrafts().filter((d: any) => getStatusLabelKey(d?.status) !== 'cancelled');
    const hasDrafts = drafts.length > 0;

    return (
      <div className="space-y-6">
        {/* Title and Subtitle */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('steps.prepare.title', 'Title')}
          </h1>
          <h2 className="text-xl text-gray-600 mb-6">
            {t('steps.prepare.subtitle', 'Subtitle')}
          </h2>
        </div>

        {/* Draft Warning Alert */}
        {hasDrafts && (
          <Alert
            type="info"
            title={t('steps.prepare.draftWarning.title')}
            message={
              <div>
                <p className="mb-3">
                  {t('steps.prepare.draftWarning.message')}
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    {drafts.length === 1
                      ? t('steps.prepare.draftWarning.draftCountSingular')
                      : t('steps.prepare.draftWarning.draftCountPlural', { count: drafts.length })}
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                    {drafts.slice(0, 3).map((draft: any) => {
                      // Extract claim type from workflow data
                      const selectedType = draft.claimData?.workflow?.stepData?.['02-00-GARANTIE']?.selectedType;
                      let claimType = selectedType || draft.claimType || null;

                      // Ensure lowercase and validate claim type
                      if (claimType) {
                        claimType = claimType.toLowerCase();
                        const validTypes = ['medical', 'cancellation', 'baggage'];
                        if (!validTypes.includes(claimType)) {
                          claimType = null;
                        }
                      }

                      // Get event number for display
                      const eventNumber = draft.eventNumber || draft.id?.slice(0, 7) || '';

                      // Extract event date from stepData
                      const stepData = draft.claimData?.workflow?.stepData || {};
                      const eventDate =
                        stepData['03A-01-DETAILS1']?.eventKnownDate ||
                        stepData['03A-01-DETAILS2']?.eventKnownDate ||
                        stepData['03A-01-DETAILS3']?.eventKnownDate ||
                        stepData['03A-01-DETAILS4']?.eventKnownDate ||
                        stepData['03A-01-DETAILS5']?.eventKnownDate;

                      return (
                        <li key={draft.id}>
                          {claimType ? t(`progress.banner.claimTypes.${claimType}`) : t('common.claim', 'Claim')} - {eventNumber}
                          {eventDate && ` - ${t('home.claims.eventDate', 'Date de l\'évènement')}: ${new Date(eventDate).toLocaleDateString()}`}
                          {' - '}{t('home.claims.modifiedOn', 'Modified on')}: {new Date(draft.lastSaved || Date.now()).toLocaleDateString()}
                        </li>
                      );
                    })}
                    {drafts.length > 3 && (
                      <li className="text-gray-500">
                        {t('steps.prepare.draftWarning.andMore', { count: drafts.length - 3 })}
                      </li>
                    )}
                  </ul>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleViewDrafts}
                >
                  {t('steps.prepare.draftWarning.viewDrafts')}
                </Button>
              </div>
            }
          />
        )}

      {/* Information Points */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Estimated Time */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-start space-x-3">
              <Clock className="w-6 h-6 text-tenant-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  {t('steps.prepare.info.time.title', 'Title')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('steps.prepare.info.time.description', 'Description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Auto-Save */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-start space-x-3">
              <Save className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  {t('steps.prepare.info.autosave.title', 'Title')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('steps.prepare.info.autosave.description', 'Description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Fraud Warning */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  {t('steps.prepare.info.fraud.title', 'Title')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('steps.prepare.info.fraud.description', 'Description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Medical Expenses */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-start space-x-3">
              <DollarSign className="w-6 h-6 text-tenant-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  {t('steps.prepare.info.medical.title', 'Title')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('steps.prepare.info.medical.description', 'Description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderNovaClaimWarning = () => (
    <div className="space-y-4">
      <Alert
        type="warning"
        title={t('steps.prepare.novaClaim.title', 'Title')}
        message={t('steps.prepare.novaClaim.message', 'Message')}
      />

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">
            {t('steps.prepare.novaClaim.existingClaimsTitle', 'Existingclaimstitle')}
          </h3>
        </div>
        <div className="card-body space-y-3">
          {checkResult.claims?.map((claim) => (
            <div
              key={claim.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-tenant-300 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <span className="font-semibold text-gray-900">
                      {claim.caseNumber || claim.id}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      claim.status === 'DRAFT'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-tenant-100 text-tenant-800'
                    }`}>
                      {claim.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>
                      {t('common.amount')}: {claim.totalAmountCAD?.toFixed(2)} CAD
                    </div>
                    {claim.submittedAt && (
                      <div>
                        {t('common.submitted')}: {formatDate(claim.submittedAt)}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleContinueNovaClaim(claim)}
                >
                  <ChevronRight className="w-4 h-4" />
                  {t('steps.prepare.novaClaim.continueClaim', 'Continueclaim')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center">
        <Button
          variant="secondary"
          onClick={handleContinueNewClaim}
        >
          {t('steps.prepare.novaClaim.startNew', 'Startnew')}
        </Button>
      </div>
    </div>
  );

  const renderDraftsFound = () => (
    <div className="space-y-4">
      <Alert
        type="info"
        title={t('steps.prepare.drafts.title', 'Title')}
        message={t('steps.prepare.drafts.message', { count: checkResult.drafts?.length || 0 })}
      />

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">
            {t('steps.prepare.drafts.yourDrafts', 'Yourdrafts')}
          </h3>
        </div>
        <div className="card-body space-y-3">
          {checkResult.drafts?.map((draft) => (
            <div
              key={draft.id}
              className={`border rounded-lg p-4 transition-all ${
                selectedDraft === draft.id
                  ? 'border-tenant-500 bg-tenant-50'
                  : 'border-gray-200 hover:border-tenant-300'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <span className="font-semibold text-gray-900">
                      {t('steps.prepare.drafts.draftTitle', { id: draft.id.substring(0, 8) })}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {t('steps.prepare.drafts.lastSaved', 'Lastsaved')}: {formatDate(draft.lastSaved)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleResumeDraft(draft.id)}
                  >
                    <ChevronRight className="w-4 h-4 mr-1" />
                    {t('steps.prepare.drafts.resume', 'Resume')}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteDraft(draft.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">
                    {t('navigation.progress', { percent: draft.progress })}
                  </span>
                  <span className="text-xs font-semibold text-tenant-600">
                    {draft.progress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-tenant-600 h-2 rounded-full transition-all"
                    style={{ width: `${draft.progress}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center">
        <Button
          variant="secondary"
          onClick={handleContinueNewClaim}
        >
          {t('steps.prepare.drafts.startNew', 'Startnew')}
        </Button>
      </div>
    </div>
  );

  if (isChecking) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <RefreshCw className="w-12 h-12 text-tenant-600 animate-spin" />
            <p className="text-lg text-gray-600">
              {t('steps.prepare.checking', 'Checking')}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Render based on check result */}
        {checkResult.type === 'NONE' && (
          <>
            {renderInfoSection()}

            <div className="mt-8 flex justify-center">
              <Button
                variant="primary"
                size="lg"
                onClick={handleContinueNewClaim}
              >
                {t('steps.prepare.startNewClaim')}
              </Button>
            </div>
          </>
        )}

        {checkResult.type === 'NOVA' && renderNovaClaimWarning()}

        {checkResult.type === 'DRAFT' && renderDraftsFound()}
      </div>
    </Layout>
  );
};

export default PrepareClaimScreen;
